

function stripColorCode(rawName) {
  return (rawName || "").split(" - ")[0].trim();
}

class CompleteTheLookCard {
  constructor(cardEl) {
    this.card = cardEl;
    this.hasColorOption = cardEl.dataset.hasColorOption === "true";
    this.colorOptionPosition = Number(
      cardEl.dataset.colorOptionPosition,
    );
    this.visibleOptionCount = Number(
      cardEl.dataset.visibleOptionCount,
    );

    this.variantsData = this._parseVariants();

    this.hiddenColorInput = cardEl.querySelector(
      "[data-ctl-color-value]",
    );
    this.unavailableMessage = cardEl.querySelector(
      "[data-ctl-unavailable-message]",
    );
    this.variantIdInput = cardEl.querySelector(
      "[data-ctl-selected-variant-id]",
    );
    this.atcButton = cardEl.querySelector("[data-ctl-atc]");
    this.atcLabel = this.atcButton?.querySelector("add-to-cart") || null;
    this.defaultLabel = this.atcLabel?.textContent.trim() || "";
    this.addingLabel = cardEl.dataset.localeAdding || "Adding...";
    this.addedLabel = cardEl.dataset.localeAdded || "Added";
    this.isAdding = false;
    this.revertTimeout = null;

    cardEl.addEventListener("change", (event) => {
      if (event.target.matches("[data-ctl-option-input]")) {
        this.refresh();
      }
    });

    this.form = cardEl.querySelector(".complete-the-look__form");

    this.form?.addEventListener("submit", () => {
      this.setAddToCartState("adding");
    });

    this.handleCartUpdate = () => {
      if (this.isAdding) {
        this.setAddToCartState("added");
      }
    };

    document.addEventListener("updateCart", this.handleCartUpdate);

    this.refresh();
  }

  setAddToCartState(state) {
    if (!this.atcLabel) return;

    clearTimeout(this.revertTimeout);

    if (state === "adding") {
      this.isAdding = true;
      this.atcLabel.textContent = this.addingLabel;

      this.revertTimeout = setTimeout(() => {
        this.setAddToCartState("idle");
      }, 8000);
    } else if (state === "added") {
      this.atcLabel.textContent = this.addedLabel;

      this.revertTimeout = setTimeout(() => {
        this.setAddToCartState("idle");
      }, 2000);
    } else {
      this.isAdding = false;
      this.atcLabel.textContent = this.defaultLabel;
    }
  }

  destroy() {
    document.removeEventListener("updateCart", this.handleCartUpdate);
    clearTimeout(this.revertTimeout);
  }

  _parseVariants() {
    const script = this.card.querySelector("[data-ctl-variants]");

    if (!script) {
      return { variants: [], optionCount: 0 };
    }

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error(
        "Unable to parse Complete the Look variant data.",
        error,
      );

      return { variants: [], optionCount: 0 };
    }
  }

  getSelectedOptions() {
    return Array.from(
      this.card.querySelectorAll("[data-ctl-option-input]:checked"),
    ).map((input) => ({
      position: Number(input.dataset.optionPosition),
      name: input.dataset.optionValue,
    }));
  }

  findMatchingVariant(colorValue) {
    const selectedOptions = this.getSelectedOptions();

    const requiredCount =
      this.visibleOptionCount + (this.hasColorOption ? 1 : 0);
    const providedCount =
      selectedOptions.length +
      (this.hasColorOption && colorValue ? 1 : 0);

    if (providedCount < requiredCount) return null;

    return (
      this.variantsData.variants.find((variant) => {
        const optionsMatch = selectedOptions.every(
          (selectedOption) => {
            const variantOption = variant.options.find(
              (option) => option.position === selectedOption.position,
            );

            return (
              variantOption &&
              variantOption.name === selectedOption.name
            );
          },
        );

        if (!optionsMatch) return false;

        if (this.hasColorOption && colorValue) {
          const colorOption = variant.options.find(
            (option) => option.position === this.colorOptionPosition,
          );

          return colorOption && colorOption.name === colorValue;
        }

        return true;
      }) || null
    );
  }

  setMainColor(mainColorName) {
    if (!this.hasColorOption) return;

    const variantColors = new Set();

    this.variantsData.variants.forEach((variant) => {
      const colorOption = variant.options.find(
        (option) => option.position === this.colorOptionPosition,
      );

      if (colorOption) variantColors.add(colorOption.name);
    });

    const normalizedMain = stripColorCode(mainColorName).toLowerCase();

    const matched = Array.from(variantColors).find(
      (name) => stripColorCode(name).toLowerCase() === normalizedMain,
    );

    if (this.hiddenColorInput) {
      this.hiddenColorInput.value = matched || "";
    }

    if (this.unavailableMessage) {
      this.unavailableMessage.hidden = Boolean(matched);
    }

    this.refresh();
  }

  refresh() {
    const colorValue = this.hasColorOption
      ? this.hiddenColorInput?.value || ""
      : "";

    const colorSatisfied = !this.hasColorOption || Boolean(colorValue);

    const variant = colorSatisfied
      ? this.findMatchingVariant(colorValue)
      : null;

    if (this.variantIdInput && variant) {
      this.variantIdInput.value = variant.id;
    }

    if (this.atcButton) {
      this.atcButton.disabled = !variant || variant.available === false;
    }
  }
}

class CompleteTheLook extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(
      this.querySelectorAll("[data-complete-the-look-card]"),
    ).map((cardEl) => new CompleteTheLookCard(cardEl));

    this.handleMainColorChange = (event) => {
      const input = event.target;

      if (!input.matches("[data-product-option-input]")) return;
      if (!input.closest("[data-product-form-selectors]")) return;
      if (input.closest("[data-complete-the-look-card]")) return;

      const optionName = input.dataset.optionName || "";

      if (optionName.toLowerCase() !== "color") return;

      this.updateMainColor(input.dataset.optionValue || "");
    };

    document.addEventListener("change", this.handleMainColorChange);

    this.handleSwatchClick = (event) => {
      const swatch = event.target.closest("[data-ctl-swatch]");

      if (!swatch || !this.contains(swatch)) return;

      this.selectColorway(swatch);
    };

    this.addEventListener("click", this.handleSwatchClick);

    this.updateMainColor(this.dataset.mainColor || "");

    if (this.dataset.hasMultiple !== "true") return;

    window.loadSwiper.then(({ Swiper, Navigation }) => {

      if (this.swiper) return;

      const container = this.querySelector(
        ".complete-the-look__container",
      );

      if (!container) return;

      const prevEl = this.querySelector(
        "[data-complete-the-look-navigation-previous]",
      );

      const nextEl = this.querySelector(
        "[data-complete-the-look-navigation-next]",
      );

      this.swiper = new Swiper(container, {
        modules: [Navigation],

        slidesPerView: 1,
        slidesPerGroup: 1,

        autoHeight: true,

        observer: true,
        observeParents: true,
        navigation:
          prevEl && nextEl ? { prevEl, nextEl } : undefined,
      });
    });
  }

  disconnectedCallback() {
    document.removeEventListener(
      "change",
      this.handleMainColorChange,
    );

    this.removeEventListener("click", this.handleSwatchClick);

    this.cards.forEach((card) => card.destroy());

    this.swiper?.destroy(true, true);
    this.swiper = null;
  }

  selectColorway(swatch) {
    const group = swatch.closest("[data-ctl-card-group]");

    if (!group) return;

    const targetId = swatch.dataset.ctlSwatchTarget;

    group
      .querySelectorAll("[data-ctl-swatch]")
      .forEach((swatchButton) => {
        const isActive =
          swatchButton.dataset.ctlSwatchTarget === targetId;

        swatchButton.classList.toggle("is-active", isActive);
        swatchButton.setAttribute(
          "aria-pressed",
          String(isActive),
        );
      });

    group.querySelectorAll("[data-ctl-colorway]").forEach((colorway) => {
      colorway.hidden = colorway.dataset.ctlColorwayId !== targetId;
    });
  }

  updateMainColor(colorName) {

    const cleanColorName = stripColorCode(colorName);

    this.dataset.mainColor = cleanColorName;
    this.cards.forEach((card) => card.setMainColor(cleanColorName));
  }
}

if (!customElements.get("complete-the-look")) {
  customElements.define("complete-the-look", CompleteTheLook);
}
